import Intercom, { shutdown, update as updateMessenger } from '@intercom/messenger-js-sdk'
import { useEffect, useRef } from 'react'
import { useLocation } from 'router-v6'
import { getIntercomAppId } from 'v2/configs/constants'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import type { RUserSession } from 'v2/types/api/auth'
import type { ROrganization, RUserProfile } from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'
import dayjs from 'v2/utils/dayjs'
import { PrimaryEvents, primaryEventBus } from 'v2/utils/event-bus/primary'
import { useFeatureFlags } from './use-feature-flags'

interface IntercomAttributes {
  [key: string]: string | number | boolean | undefined
}

/**
 * Pushes attributes onto the current contact and logs an impression at the current URL.
 * Called with `use_case` once the signup questionnaire is submitted — that is
 * the only moment the browser ever sees the use-case, since the backend logs it but never
 * persists it (contacto-core `complete_onboarding` stores only `onboarding_completed`).
 */
export const updateIntercom = (attributes: IntercomAttributes = {}) => {
  if (!getIntercomAppId() || !window.Intercom) return
  updateMessenger(attributes)
}

export const unixNow = () => Math.floor(Date.now() / 1000)

// Unix SECONDS, and undefined rather than a fallback: dayjs(undefined) is "now", which
// would claim the user signed up this second.
const unixSeconds = (date?: string) => {
  if (!date) return undefined
  const parsed = dayjs(date)
  return parsed.isValid() ? parsed.unix() : undefined
}

/**
 * Intercom does not notice URL changes in a single-page app, and it discards any update
 * carrying no changed data within a 60s window — so a bare update() on navigation is
 * dropped and nothing page-specific works. A fresh last_request_at is the field
 * Intercom's own single-page-app guide prescribes for this.
 *
 * Short delay, not a long interval: Intercom re-evaluates launcher display conditions
 * only when it receives one of these pings, so a slow ping leaves the launcher visible
 * on a page it is configured to hide on (measured: it neither appeared nor disappeared
 * for a minute). Debounced so that clicking through several screens sends one ping, for
 * the screen actually landed on, which is also what keeps us inside Intercom's limit of
 * 20 updates per user per page.
 */
export const ROUTE_IMPRESSION_DEBOUNCE_MS = 1000

/** undefined rather than an empty or half-padded string when neither part is set. */
const fullName = (profile: RUserProfile) =>
  [profile.account_first_name, profile.account_last_name].filter(Boolean).join(' ') || undefined

/** The exact attribute payload Intercom receives. Pure, so the wire contract is testable. */
export const buildIntercomSettings = (
  profile: RUserProfile,
  org: ROrganization,
  user: RUserSession,
) => ({
  app_id: getIntercomAppId(),
  // Bottom-LEFT, under the sidebar nav. Bottom-right is where the agent builder puts its
  // Save button and the launcher sat on top of it, breaking the flow smoke test. The
  // sidebar's lower area is empty on every page, so one position works everywhere.
  alignment: 'left',
  // aom_uuid is what analytics already identifies on, so contacts line up with PostHog.
  user_id: user?.aom_uuid,
  // The session name is not always populated — contacts were landing with an empty Name.
  // Fall back to the profile pair, which is how the app composes a display name elsewhere.
  name: user?.name || fullName(profile),
  // Split as well as whole: outbound messages address people by first name.
  first_name: profile.account_first_name,
  last_name: profile.account_last_name,
  email: user?.email,
  // The user's own signup, not the org's — a teammate may have joined much later.
  created_at: unixSeconds(profile.created_at),
  org_created_at: unixSeconds(org.org_created_at),
  org_name: org.name,
  org_auth_id: org.org_auth_id,
  // NOT `region` — that key is Intercom's own data-centre selector.
  plivo_region: user?.region,
  roles: user?.roles?.join(','),
  plan: profile.plan,
})

/**
 * Every domain scope a cookie on this host could have been written under. Intercom scopes
 * to the registrable domain, so expiring on the exact hostname alone leaves the
 * parent-domain copy behind; we stop before the bare TLD. Unmatched scopes are inert, so
 * over-covering is the safe direction. Exported because happy-dom ignores cookie domains,
 * making this the only way to actually test the derivation.
 */
export const cookieScopes = (hostname: string) => {
  const labels = hostname.split('.')
  const scopes = ['', `; domain=${hostname}`]
  for (let i = 1; i < labels.length - 1; i += 1) {
    scopes.push(`; domain=.${labels.slice(i).join('.')}`)
  }
  return scopes
}

/**
 * Expires every intercom- cookie across the host and its parent domains. Unconditional:
 * the intercom-id- anonymous identifier outlives the page, so a session we never booted
 * can still be holding the previous user's identity on a shared browser.
 */
export const clearIntercomCookies = () => {
  const scopes = cookieScopes(window.location.hostname)

  for (const cookie of document.cookie.split(';')) {
    const name = cookie.split('=')[0]?.trim()
    if (!name?.startsWith('intercom-')) continue
    for (const scope of scopes) {
      // biome-ignore lint/suspicious/noDocumentCookie: the Cookie Store API is async; this must finish inside the synchronous logout teardown.
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${scope}`
    }
  }
}

/** Boots the Intercom messenger for the signed-in user, and keeps it in step with the session. */
export const useIntercom = () => {
  const { data: profile } = useProfileQuery()
  const { onboardingAssistantEnabled } = useFeatureFlags()
  const location = useLocation()
  const isInitialRoute = useRef(true)

  const organization = profile?.organizations?.[0]
  const orgAuthId = organization?.org_auth_id

  useEffect(() => {
    // Two gates, not one: the app id decides whether the workspace is configured at all,
    // the flag decides which orgs see it. Prod ships with an app id and the flag off.
    // Never inside a partner's embed iframe.
    if (!getIntercomAppId() || isEmbedMode() || !onboardingAssistantEnabled) return
    // Staff are deliberately not blocked: the flag only ever enables our own org, so a
    // @plivo.com block would leave nobody. We exclude ourselves from campaigns in Intercom.
    if (!profile || !organization) return
    Intercom(buildIntercomSettings(profile, organization, getLocalAuth()))
    // Keyed on scalars, not the org object, so a deep-equal profile refetch cannot
    // re-initialise the messenger. pathname is deliberately absent — the effect below
    // owns route changes, and re-initialising on every navigation is wasteful.
  }, [orgAuthId, profile?.created_at, profile?.plan, onboardingAssistantEnabled])

  useEffect(() => {
    // Skipped on mount: the boot call above already logs an impression at this URL.
    if (isInitialRoute.current) {
      isInitialRoute.current = false
      return
    }
    const timer = setTimeout(
      () => updateIntercom({ last_request_at: unixNow() }),
      ROUTE_IMPRESSION_DEBOUNCE_MS,
    )
    return () => clearTimeout(timer)
  }, [location.pathname])

  useEffect(
    () =>
      // The same seam the shared worker and PubNub use to tear down on logout. Without
      // this, the intercom-id- cookie carries one user's history into the next user's
      // contact record on a shared browser.
      primaryEventBus.on(PrimaryEvents.LOGOUT, () => {
        if (window.Intercom) shutdown()
        clearIntercomCookies()
      }),
    [],
  )
}
